import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "../../../components/ui/form";
import { useToast } from "../../../hooks/use-toast";
import { FormInput } from "../../../components/form-components/form-input.components";
import { useEffect } from "react";
import Spinner from "../../../components/spinner.components";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Separator } from "../../../components/ui/separator";
import { Button } from "../../../components/ui/button";
import { Plus } from "lucide-react";
import BankIcon from "../../../assets/svgs/common/bank.svg";
import { useGetVendorByIdAdminHttp } from "../../../hooks/http/admin/vendors/get-vendor-by-id.admin.http";
import { handleHttpError } from "../../../utils/utils";
import { useGetAllPayoutMethodsHttp } from "../../../hooks/http/admin/vendors/payouts/get-all-payout-methods.admin.http";
import useVendorStore from "../../../store/vendor.store";
import usePayoutMethodsStore from "../../../store/payout-methods.store";

const FormSchema = z.object({
  name: z.string().min(1, { message: "Enter vendor name" }),
  email: z.string().email({ message: "Invalid email address" }),
  pan: z
    .string()
    .min(10, { message: "Enter valid PAN number" })
    .max(10, { message: "Enter valid PAN number" }),
  gst: z
    .string()
    .min(15, { message: "Enter valid GST number" })
    .max(15, { message: "Enter valid GST number" }),
});

export default function VendorOverviewAdminPartial() {
  const { vendorId } = useParams();
  const { toast } = useToast();
  const { execute: getVendorByIdHttp, fetching: fetchingVendor } =
    useGetVendorByIdAdminHttp();
  const { execute: getAllPayoutMethodsHttp, fetching: fetchingPayoutMethods } =
    useGetAllPayoutMethodsHttp();
  const navigate = useNavigate();
  const { data: vendor, setData: setVendor } = useVendorStore();
  const {
    data: payoutMethods,
    setData: setPayoutMethods,
  } = usePayoutMethodsStore();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setPayoutMethods([]);
    setVendor(null);
    fetchVendorById();
    getPayoutMethods();
  }, []);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      email: "",
      pan: "",
      gst: "",
    },
  });

  // Show form validation errors as toast
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];
      if (key) {
        toast({
          content:
            form.formState.errors[key as keyof typeof form.formState.errors]
              ?.message || "",
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors, toast]);

  useEffect(() => {
    if (vendor) {
      form.reset({
        name: vendor.name,
        email: vendor.email,
        pan: vendor.pan?.pan,
        gst: vendor.gst?.gstin,
      });
    }
  }, [vendor, form]);

  async function fetchVendorById() {
    if (!vendorId) {
      return;
    }
    const response = await getVendorByIdHttp({
      body: null,
      pathParams: { vendorId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setVendor(response.data.vendor);
    form.reset({
      name: response.data.vendor.name,
      email: response.data.vendor.email,
      pan: response.data.vendor.pan?.pan,
      gst: response.data.vendor.gst?.gstin,
    });
  }

  async function getPayoutMethods() {
    if (!vendorId) {
      return;
    }
    const response = await getAllPayoutMethodsHttp({
      body: null,
      pathParams: { vendorId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setPayoutMethods(response.data.payoutMethods);
  }

  if (fetchingVendor || fetchingPayoutMethods) {
    return (
      <div className="flex justify-center items-center w-full h-screen">
        <Spinner width={20} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-5 pt-20 w-full h-full">
      <Form {...form}>
        <div className="gap-12 grid grid-cols-1 lg:grid-cols-2 w-full">
          {/* Vendor Name */}
          <FormInput
            name="name"
            label="Vendor name"
            placeholder="Enter vendor name"
            form={form}
            type="text"
            disabled
          />

          {/* Vendor Email */}
          <FormInput
            name="email"
            label="Vendor email"
            placeholder="Enter vendor email"
            form={form}
            type="email"
            disabled
          />

          <Separator className="col-span-2" />

          {form.getValues("pan") ? (
            <FormInput
              name="pan"
              label="PAN"
              placeholder="Enter PAN number"
              form={form}
              type="text"
              disabled
            />
          ) : (
            <div className="flex flex-col gap-1.5">
              <span className="font-semibold text-sm">PAN</span>

              <Button
                onClick={() => navigate("add-pan")}
                variant="outline"
                className="flex items-center gap-2 bg-transparent hover:bg-transparent py-7 border-dashed rounded-xl hover:text-white"
              >
                <Plus /> Add PAN
              </Button>
            </div>
          )}

          {/* GST Number */}
          {form.getValues("gst") ? (
            <FormInput
              name="gst"
              label="GST number"
              placeholder="Enter GST number"
              form={form}
              type="text"
            />
          ) : (
            <div className="flex flex-col gap-1.5">
              <span className="font-semibold text-sm">GST</span>

              <Button
                onClick={() => navigate("add-gst")}
                variant="outline"
                className="flex items-center gap-2 bg-transparent hover:bg-transparent py-7 border-dashed rounded-xl hover:text-white"
              >
                <Plus /> Add GST
              </Button>
            </div>
          )}
          <Separator className="col-span-2" />

          {payoutMethods && payoutMethods.length > 0 ? (
            <div className="flex flex-col gap-4 col-span-2 pb-10 w-full">
              <div className="flex justify-between items-center w-full">
                <p>Payout details</p>
                <Button
                  className="flex gap-1 bg-primarybuttonbg text-black"
                  onClick={() => navigate("add-payout")}
                >
                  <Plus className="w-4 h-4" /> Add payout
                </Button>
              </div>
              {payoutMethods.map((payout) => {
                return (
                  <div
                    key={`${payout.id}`}
                    className="flex justify-around items-center bg-tertiarybuttonbg px-8 p-5 border border-border rounded-xl"
                  >
                    <BankIcon />
                    <div className="border-r border-border w-1 h-14" />
                    <div className="flex flex-col gap-1 text-left">
                      <p className="text-secondary">Acc no</p>
                      <p>{payout.bankAccountDetails?.accountNumber}</p>
                    </div>
                    <div className="border-r border-border w-1 h-14" />
                    <div className="flex flex-col gap-1 text-left">
                      <p className="text-secondary">Acc holder name</p>
                      <p>{payout.name}</p>
                    </div>
                    <div className="border-r border-border w-1 h-14" />
                    <div className="flex flex-col gap-1 text-left">
                      <p className="text-secondary">IFSC code</p>
                      <p>{payout.bankAccountDetails?.ifscCode}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5 col-span-2">
              <span className="font-semibold text-sm">Payout details</span>

              <Button
                onClick={() => navigate("add-payout")}
                variant="outline"
                className="flex items-center gap-2 bg-transparent hover:bg-transparent py-7 border-dashed rounded-xl hover:text-white"
              >
                <Plus /> Add payout
              </Button>
            </div>
          )}
        </div>
      </Form>
      <Outlet />
    </div>
  );
}
